import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {EventHandler, Handler, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {KeyboardEventHandlers} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {matchPartialMention} from '../../../../helpers/models/commentHelpers';
import {filterMentionableTeammates} from '../../../../helpers/models/teammateHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {InviteDropdownVisitorItem} from '../../../inboxes/viewer/conversation/topbar/invite/inviteDropdownVisitorItem';
import {AutocompleteDropdown} from '../../dropdowns/autocomplete/autocompleteDropdown';
import {DropdownCategory} from '../../dropdowns/dropdownCategory';
import {AsyncDropdownSource, DropdownFetcher} from '../../dropdowns/source/asyncDropdownSource';
import {MentionEditorDropdownAddTeammateCard} from './mentionEditorDropdownAddTeammateCard';
import {MentionEditorDropdownTeammateItemContainer} from './mentionEditorDropdownTeammateItemContainer';
import {MentionDropdownItemTypesEnum, MentionDropdownTypedRowValue} from './mentionEditorDropdownTypes';

/*
 * Props.
 */

export type TeammateOrContactSelectHandler = (partialText: string, aliasOrEmail: string) => void;

export interface MentionEditorDropdownContentProps {
  isTeamMember: PredicateOf<TeammateModel>;
  textBeforeCursor: string;
  getAnchorBounds: (partialText: string) => DOMRectReadOnly | undefined;
  onTeammateOrContactSelect: TeammateOrContactSelectHandler;
  fetcher: DropdownFetcher<MentionDropdownTypedRowValue>;
  mentionableTeammates: ReadonlyArray<TeammateModel>;
  /** Returned when the user has no teammates in Front and is able to add teammates. */
  navigateToAddTeammate: Handler | undefined;
  company: CompanyModel;
}

/*
 * Style.
 */

const StyledDropdownCategory = styled(DropdownCategory)`
  display: flex;
  align-items: center;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 6px;
`;

/*
 * Component.
 */

export const MentionEditorDropdownContent: FC<MentionEditorDropdownContentProps> = (props) => {
  const partialMention = matchPartialMention(props.textBeforeCursor);
  const {shouldForceHideDropdown, onDropdownRequestClose} = useShouldForceHideDropdown(partialMention);

  if (shouldForceHideDropdown) {
    return null;
  }

  if (!partialMention) {
    return null;
  }

  // Find the dropdown position.
  const anchorBounds = props.getAnchorBounds(partialMention);
  if (!anchorBounds) {
    return null;
  }

  const {onTeammateOrContactSelect} = props;

  // Handler that autocompletes the top teammate result if "Enter" is pressed before
  // the autocomplete dropdown has rendered. This is useful in poor network situations
  // or when you know the top result that the autocomplete will give you.
  // Note that "Enter" events will only reach this handler if the autocomplete dropdown
  // has not rendered, because it has its own handler that will prevent default on "Enter".
  const onEnterKeyDown: EventHandler = (event) => {
    const matchingTeammates = filterMentionableTeammates(
      props.mentionableTeammates,
      partialMention.substring(1),
      1,
    );

    // If we have a matching teammate, autocomplete the top result.
    if (matchingTeammates.length > 0) {
      event.preventDefault();
      onTeammateOrContactSelect(partialMention, matchingTeammates[0].alias);
    }
  };

  const keyHandlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.CONFIRM]: onEnterKeyDown,
  };

  const anchor = () => anchorBounds;
  const source = new AsyncDropdownSource({
    fetcher: props.fetcher,
    renderRow: (row) => renderRow(row, props.isTeamMember, onTeammateOrContactSelect, partialMention),
  });

  const {navigateToAddTeammate, company} = props;

  // This is returned only when the user has no teammates.
  if (navigateToAddTeammate) {
    return (
      <SimpleDropdown maxWidth={500} anchor={anchor} onRequestClose={onDropdownRequestClose}>
        <MentionEditorDropdownAddTeammateCard onClick={navigateToAddTeammate} company={company} />
      </SimpleDropdown>
    );
  }

  return (
    <>
      <KeyboardShortcuts handlers={keyHandlers} />
      <AutocompleteDropdown
        anchor={anchor}
        maxWidth={500}
        source={source}
        query={partialMention}
        onItemPreselect={onEnterKeyDown}
        onRequestClose={onDropdownRequestClose}
      />
    </>
  );
};

/*
 * Helpers.
 */

function useShouldForceHideDropdown(partialMention: string | null) {
  // Remember what the partial mention was at the time the user explicitely asked for the dropdown to be closed
  // and only show the dropdown again if it changes.
  const [partialMentionOnClose, setPartialMentionOnClose] = useState<string | null>(null);

  useEffect(() => {
    if (partialMentionOnClose && partialMention !== partialMentionOnClose) {
      setPartialMentionOnClose(null);
    }
  }, [partialMentionOnClose, partialMention]);

  const shouldForceHideDropdown = partialMentionOnClose !== null;
  const onDropdownRequestClose = () => setPartialMentionOnClose(partialMention);

  return {
    shouldForceHideDropdown,
    onDropdownRequestClose,
  };
}

function renderVisitorTooltip() {
  return (
    <Trans id="mention-editor-dropdown-visitor-header-tooltip">
      Guests you invite will be able to read the conversation, post comments, and edit shared drafts.
    </Trans>
  );
}

function renderRow(
  row: MentionDropdownTypedRowValue,
  isTeamMember: PredicateOf<TeammateModel>,
  onTeammateOrContactSelect: TeammateOrContactSelectHandler,
  partialMention: string,
) {
  switch (row.type) {
    case MentionDropdownItemTypesEnum.TEAMMATE_HEADER:
      return (
        <DropdownCategory key="mention-dropdown-teammate-header">
          <Trans id="mention-editor-dropdown-teammates-header">Teammates</Trans>
        </DropdownCategory>
      );
    case MentionDropdownItemTypesEnum.TEAMMATE:
      return (
        <MentionEditorDropdownTeammateItemContainer
          key={row.value.id}
          teammate={row.value}
          isTeamMember={isTeamMember(row.value)}
          onSelect={() => onTeammateOrContactSelect(partialMention, row.value.alias)}
        />
      );
    case MentionDropdownItemTypesEnum.VISITOR_HEADER:
      return (
        <StyledDropdownCategory key="mention-dropdown-visitor-header">
          <Trans id="mention-editor-dropdown-visitor-header">Guests</Trans>
          <StyledTooltipCoordinator placement="top-start" maxWidth={240} render={renderVisitorTooltip} />
        </StyledDropdownCategory>
      );
    case MentionDropdownItemTypesEnum.VISITOR:
      return (
        <InviteDropdownVisitorItem
          key={row.value.uid}
          email={row.value.handle}
          displayName={row.value.name}
          onSelect={() => onTeammateOrContactSelect(partialMention, row.value.handle)}
        />
      );
    default:
      return null;
  }
}
